//================================================================
//  Component: Add Second Round Approval Pane
//================================================================

//  Purpose: This pane will allow users to request second round approval from approvers across different roles

//  Properties:
//    - task = {An object, contains the commercial task details}
//    - addSecondRoundApprovalPaneOpen = {useState, used to determine if the pane is visible}
//    - setAddSecondRoundApprovalPaneOpen = {useState, used to toggle the visibility of the add approver pane}

//  Example:
//    <AddSecondRoundApprovalPane
//      task={task}
//      addSecondRoundApprovalPaneOpen={addSecondRoundApprovalPaneOpen}
//      setAddSecondRoundApprovalPaneOpen={setAddSecondRoundApprovalPaneOpen}
//    ></AddSecondRoundApprovalPane>    

//================================================================


//Libraries
import { useContext, useState, useReducer, useRef } from 'react';

//Contexts
import { GetUser } from '../../../../Library/GlobalContexts';

//Components
import UserSearch from '../../../../Components/UserSearch/UserSearch';

//Functions
import WriteDocument from '../../../../Library/WriteDocument';
import UploadFile from '../../../../Library/UploadFile';
import CreateRequest from '../../../../Library/CreateRequest';

//Images
import AddDisabled from '../../../../Components/Images/Icon_Add_Grey.svg';
import Clear from '../../../../Components/Images/Icon_Clear_Red.svg';
import Add from '../../../../Components/Images/Icon_Add_Teal.svg';
import Error from '../../../../Components/Images/Icon_ErrorFatal_Red.svg';
import Loading from '../../../../Components/Images/Image_Loading_Ripple.svg';

export default function AddSecondRoundApprovalPane({
  task,
  addSecondRoundApprovalPaneOpen,
  setAddSecondRoundApprovalPaneOpen
}) {

  //------------------------------------------------------
  //  useContexts
  //------------------------------------------------------

  const getUser = useContext(GetUser);

  //------------------------------------------------------
  //  useRef
  //------------------------------------------------------

  //For storing file input value
  const fileInputRef = useRef(null);

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to set pane status > 'onload', 'pending', 'error'
  const [paneStatus, setPaneStatus] = useState('onload');

  //------------------------------------------------------
  //  useReducer
  //------------------------------------------------------

  // Used to store the form inputs
  const [formData, setFormData] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      'newUserInput': '',
      'newUserGivenName': '',
      'newUserSurname': '',
      'newApproverRole': '',
      'approverErrorMessage': '',
      'approvers': [],
      'subject': '',
      'subjectErrorMessage': '',
      'body': '',
      'bodyErrorMessage': '',
      'attachedFile': undefined,
      'attachedFileErrorMessage': '',
      'formErrorMessage': ''
    }
  );

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Adds an approver to 'approvers'
  function addApprover() {

    // Check for duplicates
    if (formData.approvers.filter((object) => object.approveremail === formData.newUserInput).length > 0) {

      return setFormData({
        'approverErrorMessage': 'This approver already exists.'
      });

    }

    // Update Form Data
    formData.approvers.push({
      'approveremail': formData.newUserInput,
      'approvergivenname': formData.newUserGivenName,
      'approversurname': formData.newUserSurname,
      'approverrole': formData.newApproverRole,
    })
    formData.newUserInput = '';
    formData.newUserGivenName = '';
    formData.newUserSurname = '';
    formData.newApproverRole = '';
    formData.approverErrorMessage = '';

    setFormData(formData);

  };

  // Submit Handler
  function handleSubmit() {

    // ----------------------------------------------
    //  Validation Checks
    // ----------------------------------------------

    // OnSubmit Validation of all required fields
    let preventSubmit = false;

    // Check new approver fields
    if (formData.newUserInput.length > 0) {

      if (formData.newApproverRole.length === 0) {
        preventSubmit = true;
        formData.approverErrorMessage = 'Please select a role.'

      } else {

        formData.approvers.push({
          'approveremail': formData.newUserInput,
          'approvergivenname': formData.newUserGivenName,
          'approversurname': formData.newUserSurname,
          'approverrole': formData.newApproverRole
        });
        formData.approverErrorMessage = '';

      }

    } else {
      formData.signeeErrorMessage = '';

    }

    // Field Validation | Approvers
    if (formData.approvers.length === 0 && (formData.newUserInput.length === 0 || formData.newApproverRole.length === 0)) {
      preventSubmit = true;
      formData.approverErrorMessage = 'This field is required. Please add at least one approver.';

    } else {
      formData.approverErrorMessage = '';

    };

    // Field Validation | Subject
    if (formData.subject.length === 0) {
      preventSubmit = true;
      formData.subjectErrorMessage = 'This field is required.';

    } else {
      formData.subjectErrorMessage = '';

    };

    // Field Validation | Body
    if (formData.body.length === 0) {
      preventSubmit = true;
      formData.bodyErrorMessage = 'This field is required.';

    } else {
      formData.bodyErrorMessage = '';

    };

    setFormData(formData);

    // Check if ALL validations have passed
    if (preventSubmit === true) return;

    setPaneStatus('pending');

    // ----------------------------------------------
    //  Upload files
    // ----------------------------------------------

    return CreateRequest(formData.approvers.length)
      .then((requestIds) => {

        const filePromises = [];

        // Create/extract file ID & type
        const fileType = formData?.attachedFile?.name?.split('.')[1];
        const fileId = `${Date.now().toString()}${Math.floor(Math.random() * (99999 - 10000 + 1) + 10000)}.${fileType}`;

        // Loop through each approver and update document
        formData.approvers?.forEach((approver) => {

          // Save the task id into the approver object
          approver.requestid = requestIds[0];

          // No file attached > Return an empty string
          if (formData.attachedFile === undefined) return filePromises.push('');

          // Request signed url
          filePromises.push(
            UploadFile(`requests/${requestIds[0]}/${fileId}`, formData.attachedFile),
          );

        });

        // ----------------------------------------------
        //  Settle Promises > Write to Firestore
        // ----------------------------------------------

        Promise.all(filePromises)
          .then((urls) => {

            const writeDocPromises = [];

            // Loop through each approver and update document
            formData.approvers?.forEach((approver, index) => {

              const approvalTask = {
                'requestid': requestIds[index],
                'parentrequestid': task?.parentrequestid,
                'commercialrequestid' : task?.requestid,
                'tasktype': 'Commercial Second Round Approval',
                'taskname': (task?.applicationname === '' || task?.applicationname === undefined) ? `${approver?.approverrole} Approval` : `${approver?.approverrole} Approval for ${task?.applicationname}`,
                'taskdescription': formData?.body,
                'applicationname': task?.applicationname === '' || task?.applicationname === undefined ? '' : task?.applicationname,
                'initiativename': task?.initiativename === '' || task?.initiativename === undefined ? '' : task?.initiativename,
                'stage': '',
                'status': 'Initialised',
                'assignmentgroup': 'Commercial',
                'requesttype': '',
                'activityfeedvisible': true,
                'approvalhubvisible': true,
                'assignedto': {
                  'email': approver?.approveremail,
                  'givenname': approver?.approvergivenname,
                  'surname': approver?.approversurname
                },
                'assigneddate': new Date(),
                'createddate': new Date(),
                'createdby': {
                  'email': getUser?.emailaddress,
                  'givenname': getUser?.givenname,
                  'surname': getUser?.surname
                },
                'requestowner': {
                  'email': getUser?.emailaddress,
                  'givenname': getUser?.givenname,
                  'surname': getUser?.surname
                },
                'activityfeed': [
                  {
                    'activitydate': new Date(),
                    'actionedby': {
                      'email': getUser?.emailaddress,
                      'givenname': getUser?.givenname,
                      'surname': getUser?.surname
                    },
                    'action': 'created',
                    'comments': '',
                  },
                  {
                    'activitydate': new Date(),
                    'actionedby': {
                      'email': approver?.approveremail,
                      'givenname': approver?.approvergivenname,
                      'surname': approver?.approversurname
                    },
                    'action': 'assigned',
                    'comments': '',
                  }
                ],
                'lastmodifieddate': new Date(),
                'lastmodifiedby': {
                  'email': getUser?.emailaddress,
                  'givenname': getUser?.givenname,
                  'surname': getUser?.surname
                },

                // Approval Fields
                'emailsubject': formData?.subject,
                'emailbody': formData?.body,
                'supportingdocs': urls[index].length > 0 ?
                  [
                    {
                      'fileUrl': urls[index], // Extract the signed url from the array of promises
                      'fileId': fileId,
                      'fileName': formData?.attachedFile?.name,
                      'fileType': fileType,
                    }
                  ]
                  :
                  []
                ,
                'approverrole': approver?.approverrole
              }

              writeDocPromises.push(
                WriteDocument('requests', approvalTask?.requestid, approvalTask, true),
              );

            });

            return Promise.all(writeDocPromises)
              .then(() => {

                // Reset useStates
                setFormData({
                  'newUserInput': '',
                  'newUserGivenName': '',
                  'newUserSurname': '',
                  'newApproverRole': '',
                  'approverErrorMessage': '',
                  'approvers': [],
                  'subject': '',
                  'subjectErrorMessage': '',
                  'body': '',
                  'bodyErrorMessage': '',
                  'documents': [],
                  'documentsErrorMessage': '',
                  'formErrorMessage': ''
                })

                // Reset Status
                setPaneStatus('onload');

                // Close Pane
                setAddSecondRoundApprovalPaneOpen(false);

              })

          })

      })
      .catch((errors) => {

        console.log(errors);
        setPaneStatus('error');

      })

  }

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  if (!addSecondRoundApprovalPaneOpen) return null;

  // ---------------------------------------------------
  //  Onload
  // ---------------------------------------------------

  if (paneStatus === 'onload') {
    return (
      <div className='Pane-Background'>
        <dialog className='Pane-Container' >

          <div className='grid grid-rows-[90px_1fr_70px] items-center justify-between w-full overflow-hidden' >

            {/* ===================================== */}
            {/*  Header                               */}
            {/* ===================================== */}

            <div className='flex flex-col gap-2 p-4'>
              <h4 className='text-[22px] p-0 m-0'> Create Second Round Approval </h4>
              <label >
               Additional approvers added to this request will be notified via email.
              </label>
            </div>

            {/* ===================================== */}
            {/*  Form                                 */}
            {/* ===================================== */}

            <div className='w-[50vw] h-full flex flex-col overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner' autoComplete='off'>

              {/* ------------------------------- */}
              {/*  + Add Approver                 */}
              {/* ------------------------------- */}

              <div className='grid grid-cols-2'>

                {/*  Approver Email */}
                <div className='FormComponent-Row grid-cols-6'>
                  <label className='font-medium'> Approver Email <span className='text-[#C4314B]'>*</span> </label>
                  <UserSearch
                    selectedUser={formData}
                    setSelectedUser={setFormData}
                  ></UserSearch>
                </div>

                {/* Role */}
                <div className='grid-cols-6'>
                  <div className='flex flex-row gap-2 items-end w-full'>
                    <div className='FormComponent-Row w-full'>
                      <label className='font-medium'> Role <span className='text-[#C4314B]'>*</span> </label>
                      <select
                        className='Input-Field-Select'
                        style={{ width: '100%' }}
                        onChange={(e) => { setFormData({ 'newApproverRole': e.target.value }) }}
                        value={formData.newApproverRole}
                      >
                        <option hidden value=''>-</option>
                        <option value='Legal'>Legal</option>
                        <option value='Risk'>Risk</option>
                        <option value='Cyber'>Cyber</option>
                        <option value='Finance'>Finance</option>
                      </select>
                    </div>

                    {/* Add Button */}
                    <div className='pb-2 cursor-pointer'>
                      {
                        formData.newUserInput.length === 0 || formData.newApproverRole.length === 0?
                          <img src={AddDisabled} alt='Add'></img>:
                          <img src={Add} alt='Add' onClick={() => addApprover()}></img>
                      }
                    </div>

                  </div>
                </div>
              </div>

              {/* Error Message */}
              <label className='font-medium text-[#DE0000] ml-[5px]' hidden={formData?.approverErrorMessage.length === 0}>{formData?.approverErrorMessage}</label>

              {/* ------------------------------- */}
              {/*  Approvers List                 */}
              {/* ------------------------------- */}

              {
                formData?.approvers.map((approver, index) => (
                  <div className='grid grid-cols-2' key={index}>

                    {/*  Approver Email */}
                    <div className='FormComponent-Row grid-cols-6'>
                      <input
                        className='Input-Field-Text'
                        style={{ width: '100%' }}
                        type='text'
                        placeholder='Enter email address'
                        value={approver?.approveremail}
                        disabled
                        autoComplete='off'
                      ></input>
                    </div>


                    {/* Role */}
                    <div className='grid-cols-6'>
                      <div className='flex flex-row gap-2 items-end w-full'>
                        <div className='FormComponent-Row w-full'>
                          <select className='Input-Field-Select' style={{ width: '100%' }} disabled>
                            <option>{approver?.approverrole}</option>
                          </select>
                        </div>


                        {/* Clear Button */}
                        <div className='pb-2 cursor-pointer'>
                          <img
                            src={Clear}
                            alt='clear'
                            onClick={() => {
                              formData.approvers?.splice(index, 1);
                              setFormData(formData);
                            }
                            }></img>
                        </div>
                      </div>


                    </div>
                  </div>
                ))
              }

              {/* ------------------------------- */}
              {/*  Email Subject                  */}
              {/* ------------------------------- */}

              <div className='FormComponent-Row'>
                <label className='font-medium'>Email Subject <span className='text-[#C4314B]'>*</span> </label>
                <input
                  className={formData.subjectErrorMessage.length > 0 ? 'Input-Field-Text-Error' : 'Input-Field-Text'}
                  type='text'
                  placeholder='Enter email subject'
                  value={formData?.subject}
                  onChange={(e) => setFormData({ 'subject': e.target.value })}
                  autoComplete='off'
                ></input>

                <label className='font-medium text-[#DE0000] mb-0' hidden={formData?.subjectErrorMessage.length === 0}>{formData?.subjectErrorMessage}</label>
              </div>

              {/* ------------------------------- */}
              {/*  Email Body                     */}
              {/* ------------------------------- */}

              <div className='FormComponent-Row'>
                <label className='font-medium'> Email Body <span className='text-[#C4314B]'>*</span> </label>
                <textarea
                  className={formData.bodyErrorMessage.length > 0 ? 'Input-Field-TextArea-Error' : 'Input-Field-TextArea'}
                  style={{ height: '200px' }}
                  type='text'
                  placeholder='Enter email body'
                  onChange={(e) => setFormData({ 'body': e.target.value })}
                  value={formData?.body}
                  autoComplete='off'
                ></textarea>

                <label className='font-medium text-[#DE0000] mb-0' hidden={formData?.bodyErrorMessage.length === 0}>{formData?.bodyErrorMessage}</label>
              </div>

              {/* ------------------------------- */}
              {/*  Document                       */}
              {/* ------------------------------- */}

              <div className='FormComponent-Row mb-[3%]'>
                <label className='font-medium mb-1'> Attach Document </label>

                <div className='Attach-File-Onload-Container'>

                  {/* File Input Field */}
                  <input
                    className={formData?.attachedFileErrorMessage?.length > 0 ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                    style={{ padding: '0px' }}
                    type='file'
                    accept='.pdf,.doc,.txt,.xlsx,.docx,.csv,.png,.jpg'
                    ref={fileInputRef}
                    onChange={(evt) => {

                      const fileObject = evt.target.files[0];

                      // Validate File Types
                      const allowedFileTypes = ['pdf', 'doc', 'txt', 'xlsx', 'docx', 'csv', 'png', 'jpg'];

                      let fileType;
                      fileType = fileObject?.name.split('.');
                      fileType = fileType[fileType?.length - 1].toLowerCase();

                      if (allowedFileTypes.includes(fileType) === false) {
                        return setFormData({ 'attachedFileErrorMessage': `Please upload a file in the following format(s): ${allowedFileTypes.join(', ')}.` });

                      }

                      // Validate File Size < 20 MB
                      if (fileObject.size > 20000000) {
                        return setFormData({ 'attachedFileErrorMessage': 'File size too large. Please upload a file smaller than 20MB.' });

                      }

                      setFormData({
                        'attachedFile': fileObject,
                        'attachedFileErrorMessage': ''
                      });

                    }}
                  ></input>

                  {/* Clear Button */}
                  <button className='Primary-Button' disabled={formData?.attachedFile === undefined} onClick={() => {

                    // Clear File Input Field
                    if (fileInputRef.current) {

                      fileInputRef.current.value = '';
                      setFormData({
                        'attachedFile': undefined,
                        'attachedFileErrorMessage': ''
                      });

                    }
                  }}>
                    Clear
                  </button>

                </div>

                {/* Error Message */}
                <label className='font-medium text-[#DE0000]' hidden={formData?.attachedFileErrorMessage.length === 0}>{formData?.attachedFileErrorMessage}</label>

              </div>

            </div>

            {/* ------------------------------- */}
            {/*  Submit Buttons                 */}
            {/* ------------------------------- */}

            <div className='px-4 py-3 flex gap-2'>
              <button
                className='Primary-Button'
                onClick={() => handleSubmit()}
                disabled={formData.subject.length === 0 || formData.body.length === 0 || (formData.approvers.length === 0 && (formData.newUserInput.length === 0 || formData.newApproverRole.length === 0))}
              >
                Submit
              </button>
              <button className='Secondary-Button' onClick={() => {
                setFormData({
                  'newUserInput': '',
                  'newUserGivenName': '',
                  'newUserSurname': '',
                  'newApproverRole': '',
                  'approverErrorMessage': '',
                  'approvers': [],
                  'subject': '',
                  'subjectErrorMessage': '',
                  'body': '',
                  'bodyErrorMessage': '',
                  'documents': [],
                  'documentsErrorMessage': '',
                  'formErrorMessage': ''
                });
                setAddSecondRoundApprovalPaneOpen(false)
              }}>
                Cancel
              </button>
            </div>

          </div>

        </dialog>
      </div>
    )
  }

  // ---------------------------------------------------
  //  Pending
  // ---------------------------------------------------

  else if (paneStatus === 'pending') {
    return (
      <div className='Pane-Background'>
        <dialog className='Pane-Container flex justify-center items-center text-center'>
          <img alt='loading-circle-icon' src={Loading}></img>
        </dialog>
      </div>
    )
  }

  // ---------------------------------------------------
  //  Error
  // ---------------------------------------------------

  else if (paneStatus === 'error') {
    return (
      <div className='Pane-Background'>

        {/* Pane Container */}
        <dialog className='Pane-Container'>
          <div className='flex flex-col gap-2 w-full justify-center items-center'>

            <img className='my-2 w-[100px]' src={Error} alt='Error'></img>
            <h4 className='mb-0'>Oops! Something went wrong.</h4>
            <p className='text-center leading-[1.7]'>
              An error occurred while we processed your request.
            </p>
            <button
              className='Primary-Button'
              onClick={() => {
                setFormData({
                  'newUserInput': '',
                  'newUserGivenName': '',
                  'newUserSurname': '',
                  'newApproverRole': '',
                  'approverErrorMessage': '',
                  'approvers': [],
                  'subject': '',
                  'subjectErrorMessage': '',
                  'body': '',
                  'bodyErrorMessage': '',
                  'documents': [],
                  'documentsErrorMessage': '',
                  'formErrorMessage': ''
                })
                setPaneStatus('onload');
                setAddSecondRoundApprovalPaneOpen(false);
              }}
            >
              Close
            </button>

          </div>
        </dialog>
      </div>

    )
  }

  //------------------------------------------------------
}
